import { useState, useEffect } from 'react';

interface TermsStepProps {
  onSubmit: () => void;
  onBack: () => void;
}

const termContents: Record<string, { title: string; content: string }> = {
  service: {
    title: '서비스 이용약관',
    content: `제1조 (목적)
본 약관은 코리아케어러스(이하 "회사")가 제공하는 간병인 매칭 서비스(이하 "서비스")의 이용과 관련하여 회사와 회원 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.

제2조 (정의)
1. "서비스"란 회사가 제공하는 간병인-환자 매칭 플랫폼 및 관련 부가서비스를 의미합니다.
2. "회원"이란 본 약관에 동의하고 회사와 서비스 이용계약을 체결한 자를 의미합니다.
3. "간병인"이란 서비스를 통해 간병 서비스를 제공하는 회원을 의미합니다.
4. "환자/보호자"란 서비스를 통해 간병 서비스를 이용하는 회원을 의미합니다.

제3조 (약관의 효력 및 변경)
회사는 필요한 경우 관련 법령을 위배하지 않는 범위 내에서 본 약관을 변경할 수 있으며, 변경 시 사전 공지합니다.

제4조 (서비스의 내용)
회사는 간병인과 환자 간의 매칭 중개 서비스를 제공하며, 간병비는 당사자 간 직접 정산합니다. 회사는 매칭 성사 후 중개 수수료를 별도로 수취합니다.

제5조 (회원의 의무)
회원은 서비스 이용 시 허위 정보를 제공해서는 안 되며, 타인의 정보를 도용하여서는 안 됩니다.`,
  },
  privacy: {
    title: '개인정보 수집 및 이용 동의',
    content: `코리아케어러스는 서비스 제공을 위해 아래와 같이 개인정보를 수집 및 이용합니다.

1. 수집하는 개인정보 항목
- 필수: 성명, 휴대폰 번호, 성별, 생년월일, 주소
- 간병인 추가: 경력, 자격증, 간병 가능 지역/시간
- 환자 추가: 간병 필요 기간, 희망 간병비, 환자 상태
- 선택: 프로필 사진, 자기소개

2. 수집 및 이용 목적
- 서비스 회원가입 및 관리
- 간병인-환자 매칭 서비스 제공
- 매칭 성사 시 상대방 연락처 제공
- 서비스 관련 고지사항 전달

3. 보유 및 이용 기간
- 회원 탈퇴 시까지 보유
- 단, 관련 법령에 따라 일정 기간 보관이 필요한 경우 해당 기간 동안 보관

4. 동의를 거부할 권리 및 거부 시 불이익
- 귀하는 개인정보 수집 및 이용에 대한 동의를 거부할 권리가 있습니다.
- 다만, 필수 항목에 대한 동의를 거부하실 경우 서비스 이용이 제한됩니다.`,
  },
  thirdParty: {
    title: '개인정보 제3자 제공 동의',
    content: `코리아케어러스는 매칭 서비스 제공을 위해 아래와 같이 개인정보를 제3자에게 제공합니다.

1. 제공받는 자: 매칭된 상대방 (간병인 ↔ 환자/보호자)
2. 제공 목적: 간병 서비스 매칭 및 원활한 서비스 진행
3. 제공 항목: 성명, 성별, 연락처(휴대폰 번호)
4. 제공 시점: 양측이 매칭을 수락한 시점
5. 보유 기간: 매칭 종료 후 30일

※ 매칭이 성사되어 양측이 모두 수락한 경우에만 상대방에게 연락처가 공개됩니다.
※ 동의를 거부하실 경우 매칭 서비스 이용이 불가능합니다.`,
  },
};

export default function TermsStep({ onSubmit, onBack }: TermsStepProps) {
  const [agreeAll, setAgreeAll] = useState(false);
  const [agreeService, setAgreeService] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeThirdParty, setAgreeThirdParty] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [expandedTerm, setExpandedTerm] = useState<string | null>(null);

  useEffect(() => {
    if (agreeService && agreePrivacy && agreeThirdParty && agreeMarketing) {
      setAgreeAll(true);
    } else {
      setAgreeAll(false);
    }
  }, [agreeService, agreePrivacy, agreeThirdParty, agreeMarketing]);

  function handleAgreeAll() {
    const next = !agreeAll;
    setAgreeAll(next);
    setAgreeService(next);
    setAgreePrivacy(next);
    setAgreeThirdParty(next);
    setAgreeMarketing(next);
  }

  function handleSingleAgree(setter: (v: boolean) => void, current: boolean) {
    setter(!current);
  }

  const canProceed = agreeService && agreePrivacy && agreeThirdParty;

  return (
    <div className="space-y-4">
      <button
        onClick={handleAgreeAll}
        className={`w-full p-4 rounded-2xl border-2 text-left cursor-pointer transition-all ${
          agreeAll ? 'border-[#2E7D6B] bg-[#f0f9f6]' : 'border-gray-200 bg-white'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
            agreeAll ? 'bg-[#2E7D6B]' : 'bg-gray-200'
          }`}>
            {agreeAll && <i className="ri-check-line text-white text-sm" />}
          </div>
          <span className="text-base font-bold text-gray-900">모두 동의합니다</span>
        </div>
        <p className="text-xs text-gray-400 mt-1.5 ml-9">
          서비스 이용약관, 개인정보 수집 및 이용, 개인정보 제3자 제공, 마케팅 수신에 모두 동의합니다.
        </p>
      </button>

      <div className="h-px bg-gray-100" />

      {[
        { key: 'service', label: '[필수] 서비스 이용약관 동의', value: agreeService, setter: () => handleSingleAgree(setAgreeService, agreeService) },
        { key: 'privacy', label: '[필수] 개인정보 수집 및 이용 동의', value: agreePrivacy, setter: () => handleSingleAgree(setAgreePrivacy, agreePrivacy) },
        { key: 'thirdParty', label: '[필수] 개인정보 제3자 제공 동의', value: agreeThirdParty, setter: () => handleSingleAgree(setAgreeThirdParty, agreeThirdParty) },
        { key: 'marketing', label: '[선택] 마케팅 수신 동의', value: agreeMarketing, setter: () => handleSingleAgree(setAgreeMarketing, agreeMarketing) },
      ].map((item) => (
        <div key={item.key} className="space-y-2">
          <div className="flex items-center justify-between">
            <button onClick={item.setter} className="flex items-center gap-3 cursor-pointer">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                item.value ? 'bg-[#2E7D6B]' : 'bg-gray-200'
              }`}>
                {item.value && <i className="ri-check-line text-white text-xs" />}
              </div>
              <span className="text-sm text-gray-700">{item.label}</span>
            </button>
            {item.key !== 'marketing' && (
              <button
                onClick={() => setExpandedTerm(expandedTerm === item.key ? null : item.key)}
                className="text-xs text-gray-400 cursor-pointer flex items-center gap-0.5"
              >
                보기
                {expandedTerm === item.key ? (
                  <i className="ri-arrow-up-s-line text-xs" />
                ) : (
                  <i className="ri-arrow-down-s-line text-xs" />
                )}
              </button>
            )}
          </div>
          {expandedTerm === item.key && termContents[item.key] && (
            <div className="ml-8 p-4 rounded-xl bg-gray-50 border border-gray-100 max-h-48 overflow-y-auto">
              <h4 className="text-sm font-bold text-gray-800 mb-2">{termContents[item.key].title}</h4>
              <p className="text-xs text-gray-500 whitespace-pre-line leading-relaxed">
                {termContents[item.key].content}
              </p>
            </div>
          )}
        </div>
      ))}

      <div className="h-px bg-gray-100" />

      {/* 액션 버튼 */}
      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="flex-1 py-3.5 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 whitespace-nowrap cursor-pointer hover:bg-gray-200 transition-colors"
        >
          이전
        </button>
        <button
          onClick={onSubmit}
          disabled={!canProceed}
          className="flex-1 py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: '#2E7D6B' }}
        >
          동의하고 계속하기
        </button>
      </div>

      {!canProceed && (
        <p className="text-xs text-red-400 text-center">필수 약관에 모두 동의해주세요.</p>
      )}
    </div>
  );
}